import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  User, 
  Mail, 
  Calendar, 
  Sparkles, 
  Package, 
  FolderOpen,
  Edit2,
  Save,
  X,
  Clock,
  ChevronRight,
  Lock,
  Eye,
  EyeOff
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProfilePage() {
  const navigate = useNavigate();
  const { isAuthenticated, token, user, loading: authLoading, refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [history, setHistory] = useState({ fonts: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  
  // Change password state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }

    if (isAuthenticated) {
      fetchProfileData();
    }
  }, [isAuthenticated, authLoading, navigate]);

  const fetchProfileData = async () => {
    setLoading(true);
    try {
      const [profileRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/api/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/api/profile/history?limit=10`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setProfile(profileRes.data);
      setHistory(historyRes.data);
      setEditName(profileRes.data.name);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!editName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }

    setIsSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/profile`,
        { name: editName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setProfile({ ...profile, name: editName.trim() });
      setIsEditing(false);
      toast.success('Profile updated!');
      
      // Refresh auth context user data
      if (refreshUser) {
        refreshUser();
      }
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async () => {
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    setIsChangingPassword(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/change-password`,
        {
          current_password: currentPassword,
          new_password: newPassword
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Password changed successfully!');
      setShowChangePassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to change password';
      toast.error(message);
    } finally {
      setIsChangingPassword(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#0D0507]">
        <Header />
        <main className="pt-24 pb-16 px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Gothic Profile Skeleton */}
            <div className="animate-gothic-fade space-y-8">
              {/* Header */}
              <div className="space-y-4">
                <div className="skeleton-text w-20 h-4" />
                <div className="skeleton-text w-48 h-12" />
              </div>
              
              {/* Profile card */}
              <div className="skeleton-card p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="skeleton-avatar w-20 h-20" />
                  <div className="space-y-2">
                    <div className="skeleton-text w-44 h-7" />
                    <div className="skeleton-text w-56 h-4" />
                  </div>
                </div>
                <div className="skeleton-text w-48 h-4" />
              </div>
              
              {/* Stats grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 stagger-cathedral">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="skeleton-card p-6">
                    <div className="flex items-center gap-4">
                      <div className="skeleton-avatar w-12 h-12" />
                      <div className="space-y-2">
                        <div className="skeleton-text w-20 h-3" />
                        <div className="skeleton-text w-12 h-8" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* History section */}
              <div className="skeleton-card">
                <div className="p-6 border-b border-[#D4AF37]/20">
                  <div className="skeleton-text w-44 h-6" />
                </div>
                <div className="divide-y divide-[#D4AF37]/10">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="p-4 flex items-center justify-between">
                      <div className="space-y-2">
                        <div className="skeleton-text w-36 h-4" />
                        <div className="skeleton-text w-60 h-3" />
                      </div>
                      <div className="skeleton-text w-20 h-3" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0507]" data-testid="profile-page">
      <Header />
      
      <main className="pt-24 pb-16 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Page Header */}
          <div className="mb-12">
            <span className="mono-label">Profile</span>
            <h1 className="text-4xl lg:text-5xl font-cinzel font-bold tracking-wide mt-4 text-[#F4EFE6]">
              Your Account
            </h1>
          </div>

          {/* Profile Card */}
          <div className="card-surface p-8 mb-8 rounded-lg">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 bg-[#722F37] flex items-center justify-center rounded-lg">
                  <span className="text-3xl font-cinzel font-bold text-[#D4AF37]">
                    {profile?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
                <div>
                  {isEditing ? (
                    <div className="flex items-center gap-2">
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="input-terminal text-2xl font-cinzel font-bold rounded-lg h-10"
                        data-testid="edit-name-input"
                      />
                      <Button
                        size="sm"
                        onClick={handleSaveProfile}
                        disabled={isSaving}
                        className="btn-primary rounded-lg h-10"
                        data-testid="save-name-btn"
                      >
                        <Save className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setIsEditing(false);
                          setEditName(profile?.name || '');
                        }}
                        className="btn-secondary rounded-lg h-10"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <h2 
                        className="text-2xl font-cinzel font-bold text-[#F4EFE6]"
                        data-testid="profile-name"
                      >
                        {profile?.name}
                      </h2>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setIsEditing(true)}
                        className="text-[#C2B49A] hover:text-[#D4AF37]"
                        data-testid="edit-name-btn"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                  <p className="text-[#C2B49A] flex items-center gap-2 mt-1 font-serif">
                    <Mail className="w-4 h-4" />
                    {profile?.email}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 text-sm text-[#C2B49A] font-serif">
              <Calendar className="w-4 h-4" />
              <span>Member since {formatDate(profile?.created_at)}</span>
            </div>
          </div>

          {/* Change Password Section */}
          <div className="card-surface p-8 mb-8 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-[#D4AF37]" />
                <h3 className="text-xl font-cinzel font-bold text-[#F4EFE6]">
                  Password & Security
                </h3>
              </div>
              {!showChangePassword && (
                <Button
                  variant="outline"
                  onClick={() => setShowChangePassword(true)}
                  className="btn-secondary rounded-lg"
                  data-testid="change-password-btn"
                >
                  Change Password
                </Button>
              )}
            </div>
            
            {showChangePassword ? (
              <div className="space-y-4 mt-6">
                <div>
                  <label className="mono-label mb-2 block">Current Password</label>
                  <div className="relative">
                    <Input
                      type={showCurrentPassword ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                      className="input-terminal rounded-lg pr-10"
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#C2B49A] hover:text-[#D4AF37]"
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="mono-label mb-2 block">New Password</label>
                  <div className="relative">
                    <Input
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password (min 6 characters)"
                      className="input-terminal rounded-lg pr-10"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#C2B49A] hover:text-[#D4AF37]"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="mono-label mb-2 block">Confirm New Password</label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="input-terminal rounded-lg"
                    data-testid="confirm-password-input"
                  />
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={handleChangePassword}
                    disabled={isChangingPassword}
                    className="btn-primary rounded-lg"
                    data-testid="save-password-btn"
                  >
                    {isChangingPassword ? 'Changing...' : 'Update Password'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowChangePassword(false);
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                    }}
                    className="btn-secondary rounded-lg"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-[#C2B49A] font-serif">
                Keep your account secure by using a strong password
              </p>
            )}
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="card-surface p-6 rounded-lg">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center gothic-border rounded-lg">
                  <Sparkles className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="mono-label">Fonts Created</p>
                  <p className="text-3xl font-cinzel font-bold text-[#F4EFE6]" data-testid="fonts-created-count">
                    {profile?.stats?.fonts_created || 0}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="card-surface p-6 rounded-lg">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center gothic-border rounded-lg">
                  <Package className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="mono-label">Fonts Purchased</p>
                  <p className="text-3xl font-cinzel font-bold text-[#F4EFE6]" data-testid="fonts-purchased-count">
                    {profile?.stats?.fonts_purchased || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="card-surface p-6 rounded-lg">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center gothic-border rounded-lg">
                  <FolderOpen className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="mono-label">Collections</p>
                  <p className="text-3xl font-cinzel font-bold text-[#F4EFE6]" data-testid="collections-count">
                    {profile?.stats?.collections || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Generation History */}
          <div className="card-surface rounded-lg">
            <div className="p-6 border-b border-[#D4AF37]/20 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-cinzel font-bold text-[#F4EFE6]">
                  Generation History
                </h3>
                <p className="text-sm text-[#C2B49A] font-serif">
                  Your recent font creations ({history.total} total)
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="btn-secondary rounded-lg"
              >
                View All
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            
            {history.fonts.length === 0 ? (
              <div className="p-12 text-center">
                <Sparkles className="w-12 h-12 text-[#C2B49A] mx-auto mb-4" />
                <p className="text-[#C2B49A] mb-4 font-serif">No fonts created yet</p>
                <Button
                  onClick={() => navigate('/generator')}
                  className="btn-primary rounded-lg"
                >
                  Create Your First Font
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-[#D4AF37]/20">
                {history.fonts.map((font) => (
                  <div 
                    key={font.id}
                    className="p-4 hover:bg-[#160A0D] transition-colors cursor-pointer"
                    onClick={() => navigate(`/font/${font.id}/preview`)}
                    data-testid={`history-font-${font.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-cinzel font-medium text-[#F4EFE6] truncate">
                          {font.name}
                        </h4>
                        <p className="text-sm text-[#C2B49A] truncate font-serif">
                          &quot;{font.prompt.slice(0, 60)}...&quot;
                        </p>
                      </div>
                      <div className="flex items-center gap-4 ml-4">
                        <span className="text-xs text-[#C2B49A] flex items-center gap-1 whitespace-nowrap font-serif">
                          <Clock className="w-3 h-3" />
                          {formatDate(font.created_at)}
                        </span>
                        <ChevronRight className="w-4 h-4 text-[#C2B49A]" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="mt-8 flex flex-wrap gap-4">
            <Button
              onClick={() => navigate('/generator')}
              className="btn-primary rounded-lg"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Create New Font
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/collections')}
              className="btn-secondary rounded-lg"
            >
              <FolderOpen className="w-4 h-4 mr-2" />
              My Collections
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="btn-secondary rounded-lg"
            >
              <Package className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
